import { FC, ReactNode } from 'react';
import { css, cx } from '@linaria/atomic';
import type { IconProps } from '../../icon/Icon.component';
import { ConfirmBadgeIcon } from '../../icon/icons/confirm-badge';
import { ErrorBadgeIcon } from '../../icon/icons/error-badge';
import { InfoBadgeIcon } from '../../icon/icons/info-badge';
import { WarningIndicator } from '../../icon/icons/warning-indicator';
import { Button } from '../button/button.component';
import { Text } from '../text/text.component';
import * as styles from './banner-notification.styles';

type BaseProps = {
  fullWidth?: boolean;
  status: 'success' | 'warn' | 'error' | 'info';
  onClose?: () => void;
};

type PropsMessage = {
  message: ReactNode;
  children?: never;
};

type PropsChildren = {
  message?: never;
  children: ReactNode;
};

type Props = (BaseProps & PropsMessage) | (BaseProps & PropsChildren);

export type BannerProps = Props & Omit<React.ComponentPropsWithoutRef<'section'>, keyof Props>;

type IconSelectionProps = Record<BannerProps['status'], FC<IconProps>>;

const IconSelection: IconSelectionProps = {
  success: ConfirmBadgeIcon,
  warn: WarningIndicator,
  error: ErrorBadgeIcon,
  info: InfoBadgeIcon,
};

const textNoMarginStyle = css`
  margin: 0;
`;

const flexOneStyle = css`
  flex: 1;
`;

export const BannerNotification = ({
  message,
  fullWidth = false,
  status,
  children,
  onClose,
  className,
  ...rest
}: BannerProps) => {
  const IconComponent = IconSelection[status];
  const iconColor = status === 'info' ? 'primary' : status;

  const displayedMessage = children || message;

  return (
    <section
      className={cx(
        styles.bannerBaseStyle,
        styles.bannerStatusStyles[status],
        fullWidth && styles.bannerFullWidthStyle,
        className
      )}
      {...rest}
    >
      <IconComponent color={iconColor} />
      <div className={flexOneStyle}>
        {typeof displayedMessage === 'string' ? (
          <Text size='medium' className={textNoMarginStyle}>
            {displayedMessage}
          </Text>
        ) : (
          displayedMessage
        )}
      </div>
      {onClose && <Button iconName='x' onClick={onClose} variant='secondary' />}
    </section>
  );
};
